import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Eye, EyeOff, Mail, MapPin, Phone, Users } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SchoolHeader from "../school/components/SchoolHeader";
import AdminSideBar from "./AdminSideBar";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const EXAMS = [
  { code: "NSTSE", selected: "IsNSTSEselected", date: "NSTSEExamdate", teacher: "NSTSETeachername", mobile: "NSTSETeachermobile", email: "NSTSETeachemail" },
  { code: "UIEO", selected: "IsUIEOSelected", date: "UIEOExamdate", teacher: "UIEOTeachername", mobile: "UIEOTeacherMobile", email: "UIEOTeacherMail" },
  { code: "UIMO", selected: "IsUIMOSelected", date: "UIMOExamdate", teacher: "UIMOTeachername", mobile: "UIMOTeacherMobile", email: "UIMOTeacherMail" },
  { code: "UCO", selected: "IsUCOSelected", date: "UCOExamdate", teacher: "UCOTeacherName", mobile: "UCOTeacherMobile", email: "UCOTeacherMail" },
  { code: "UIGKO", selected: "IsUIGKOSelected", date: "UIGKOExamdate", teacher: "UIGKOTeacherName", mobile: "UIGKOTeacherMobile", email: "UIGKOTeacherMail" },
];

const show = (value) => value || "—";

const Info = ({ label, value }) => (
  <div className="min-w-0 text-left">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-1 break-words text-sm font-medium text-slate-800">{show(value)}</p>
  </div>
);

const SelectedSchoolDetails = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const unauthorized = useCallback(() => {
    localStorage.removeItem("uc_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("uc_admin_token");
    if (!token) {
      unauthorized();
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    axios.get(`${API_BASE_URL}/school-registration/registered-schools/${schoolId}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then((response) => {
      setIsPasswordVisible(false);
      setSchool(response.data?.data?.school || null);
    }).catch((error) => {
      if (error.code === "ERR_CANCELED") return;
      if (error.response?.status === 401) return unauthorized();
      toast.error(error.response?.data?.message || "Unable to load school details");
      if (error.response?.status === 404) navigate("/admin/registered-schools");
    }).finally(() => {
      if (!controller.signal.aborted) setIsLoading(false);
    });

    return () => controller.abort();
  }, [schoolId, unauthorized, navigate]);

  const selectedExams = school ? EXAMS.filter((exam) => school[exam.selected]) : [];

  return (
    <div className="min-h-screen bg-gradient-hero">
      <SchoolHeader />
      <div className="flex flex-col bg-slate-50 lg:flex-row">
        <AdminSideBar />
        <main className="min-w-0 flex-1 px-3 py-5 sm:px-5">
          <div className="mx-auto max-w-[1500px] space-y-5">
            {isLoading && !school ? (
              <Card><CardContent className="flex h-40 items-center justify-center text-slate-500">Loading school details...</CardContent></Card>
            ) : school && (
              <>
                <Card>
                  <CardHeader className="border-b bg-slate-50 text-left">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <CardTitle className="text-md font-semibold uppercase tracking-[0.2em] text-sky-600">
                        {school.SchoolName || "Registered School"}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsPasswordVisible((visible) => !visible)}
                          aria-label={isPasswordVisible ? "Hide password" : "Password"}
                          title={isPasswordVisible ? "Hide password" : "Password"}
                        >
                          {isPasswordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          {isPasswordVisible ? show(school.Password) : "Password"}
                        </Button>
                        <Button asChild>
                          <Link to={`/admin/registered-schools/${schoolId}/students`}>
                            <Users className="h-4 w-4" />View students
                          </Link>
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => navigate("/admin/registered-schools")} aria-label="Back to registered schools">
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="grid gap-5 p-5 sm:grid-cols-2 lg:grid-cols-4">
                    <Info label="School ID" value={school.SchoolId} />
                    <Info label="School name" value={school.SchoolName} />
                    <Info label="Principal" value={school.Schoolprinciname} />
                    <Info label="Principal contact" value={school.PrinciContact} />
                    <Info label="Exam mode" value={school.Exam_Mode} />
                    <Info label="School email" value={school.SchoolMail} />
                    <Info label="School contact" value={school.SchoolContact} />
                    <Info label="Place" value={school.Place} />
                    <Info label="District" value={school.District} />
                    <Info label="State" value={school.State} />
                    <Info label="Country" value={school.Country} />
                    <Info label="Pincode" value={school.Pincode} />
                    <div className="sm:col-span-2 lg:col-span-4"><Info label="Address" value={school.SchoolAddress} /></div>
                  </CardContent>
                  <div className="flex flex-wrap gap-4 border-t px-5 py-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{show(school.Place)}</span>
                    <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />{show(school.SchoolContact)}</span>
                    <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" />{show(school.SchoolMail)}</span>
                  </div>
                </Card>

                {selectedExams.length > 0 && (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                    {selectedExams.map((exam) => (
                      <Card key={exam.code} className="text-left">
                        <CardHeader className="pb-2"><CardTitle className="text-base text-sky-700">{exam.code}</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                          <Info label="Exam date" value={school[exam.date]} />
                          <Info label="Teacher in-charge" value={school[exam.teacher]} />
                          <Info label="Mobile" value={school[exam.mobile]} />
                          <Info label="Email" value={school[exam.email]} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default SelectedSchoolDetails;
